import { getBaseRole } from '@/helpers/customRoles';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import ErrorHandling from '@/services/ErrorHandling'
import store from '@/store/redux.store'
import {
  hide as hideConfirm,
  show as showConfirm,
  update as updateConfirm,
} from '@/store/confirm-reducer'
import { refreshMessengerBindingStatus } from '@/store/messengerBindingSlice'
import { actionUpdProfile } from '@/store/profile.reducer'

const MAX_TWO_FACTOR_SKIP_PATHS = [
  'two-factor/request-code',
  'two-factor/verify-code',
  'max/check-is-active',
  'max/activate',
  'max/start-activation',
]

const MAX_TWO_FACTOR_OPERATION_TITLES: Record<string, string> = {
  employee_auth: 'Подтвердите авторизацию',
  student_action: 'Подтвердите действие с учеником',
  homework_grade: 'Подтвердите изменение оценки',
  data_export: 'Подтвердите выгрузку данных',
}

const MAX_TWO_FACTOR_CONFIRM_DESCRIPTION =
  'Перейдите в MAX-бота по ссылке, получите 6-значный код подтверждения действия и введите его здесь.'

const MAX_BINDING_DESCRIPTION =
  'Нажмите «Открыть MAX-бота», перейдите по ссылке, нажмите «Начать» в боте и введите здесь 6-значный код активации.'

const EMPLOYEE_AUTH_OPERATION = 'employee_auth'
const MAX_LOGIN_CONFIRMATION_DESCRIPTION =
  'Для продолжения работы подтвердите вход через MAX. Перейдите в MAX-бота, получите 6-значный код и введите его здесь.'

const getMaxTwoFactorConfirmDescription = (description?: string) =>
  description
    ? `${description} ${MAX_TWO_FACTOR_CONFIRM_DESCRIPTION}`
    : MAX_TWO_FACTOR_CONFIRM_DESCRIPTION

const isEnabledFlag = (value: unknown) =>
  value === true || value === 1 || value === '1'

const isCurrentUserAdmin = () =>
  getBaseRole(store.getState()?.profile?.profile?.role) === 'admin'

const reloadPageAfterMaxLoginConfirmation = () => {
  window.location.reload()
}

const getPathFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url, process.env.REACT_APP_PATH_FILE || 'http://localhost')
    const path = urlObj.pathname
    return path.startsWith('/') ? path.slice(1) : path
  } catch {
    return url
  }
}

const removeQueryString = (url: string): string => {
  if (url.includes('/?')) return url.split('/?')[0]
  if (url.includes('?')) return url.split('?')[0]
  return url
}

const normalizePath = (url = ''): string =>
  removeQueryString(getPathFromUrl(url)).replace(/^v1\//, '').replace(/\/+$/, '')

const isBinaryResponseData = (data: unknown): boolean =>
  data instanceof ArrayBuffer || data instanceof Blob

const parseBinaryResponseData = async (data: unknown): Promise<unknown> => {
  try {
    let text = ''

    if (data instanceof Blob) {
      text = await data.text()
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(data)
    }

    if (!text) return data

    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  } catch {
    return data
  }
}

const normalizeBinaryErrorResponseData = async (error: AxiosError) => {
  const response = error?.response

  if (!response || !isBinaryResponseData(response.data)) return

  response.data = await parseBinaryResponseData(response.data)
}

const getResponseStatus = (response?: AxiosResponse | AxiosError): number | undefined =>
  (response as AxiosError)?.response?.status || (response as AxiosResponse)?.status

const getResponseData = (response?: AxiosResponse | AxiosError): any =>
  (response as AxiosError)?.response?.data || (response as AxiosResponse)?.data

const getResponsePayload = (response?: AxiosResponse | AxiosError): any => {
  const data = getResponseData(response)
  return data?.data ?? data
}

const isFailedResponse = (response?: AxiosResponse | AxiosError): boolean => {
  const status = getResponseStatus(response)
  const data = getResponseData(response)

  if (!status || status < 200 || status >= 300) {
    return true
  }

  if (isBinaryResponseData(data)) {
    return false
  }

  return data?.status === 'error'
}

const getResponseErrorText = (response?: AxiosResponse | AxiosError): string => {
  const data = getResponseData(response)
  const error = data?.error || data?.message

  if (Array.isArray(error)) {
    return error.join(', ')
  }

  return error || 'Не удалось подтвердить действие через MAX'
}

const parseIntegrationFields = (fields: unknown): Record<string, unknown> => {
  if (!fields) return {}

  if (typeof fields === 'string') {
    try {
      const parsedFields = JSON.parse(fields)
      return typeof parsedFields === 'object' && parsedFields !== null
        ? parsedFields
        : {}
    } catch {
      return {}
    }
  }

  return typeof fields === 'object' ? (fields as Record<string, unknown>) : {}
}

const buildMaxBotUrl = (botId?: unknown): string | null => {
  const normalizedBotId = String(botId || '')
    .trim()
    .replace(/^@/, '')
    .replace(/^\/+/, '')

  return normalizedBotId ? `https://max.ru/${normalizedBotId}` : null
}

const getMaxBotUrl = async (api: AxiosInstance): Promise<string | null> => {
  try {
    const configResponse = await api.get('integration/get-config?name=max', {
      skipGlobalErrorAlert: true,
      skipMaxTwoFactor: true,
    } as any)

    if (isFailedResponse(configResponse)) {
      return null
    }

    const fields = parseIntegrationFields(configResponse?.data?.data?.fields)

    return buildMaxBotUrl(fields.bot_id)
  } catch {
    return null
  }
}

const isMaxTwoFactorRequiredResponse = (
  response?: AxiosResponse,
  config?: AxiosError['config'],
): boolean => {
  const data = getResponseData(response)
  const meta = data?.meta
  const errorText = getResponseErrorText(response)

  return (
    response?.status === 423 &&
    (
      meta?.requires_confirmation === true ||
      meta?.channel === 'max' ||
      errorText.toLowerCase().includes('max') ||
      inferMaxTwoFactorOperation(config) !== null
    )
  )
}

const isMaxBindingRequiredResponse = (response?: AxiosResponse): boolean => {
  const data = getResponseData(response)
  const meta = data?.meta
  const code = meta?.code || data?.errorCode

  return (
    response?.status === 423 &&
    (
      code === 'max_binding_required' ||
      meta?.action === 'open_max_binding_modal'
    )
  )
}

const inferMaxTwoFactorOperation = (config?: AxiosError['config']): string | null => {
  const method = (config?.method || 'get').toLowerCase()
  const path = normalizePath(config?.url || '')

  if (
    method === 'get' &&
    (
      /^user\/authorization\/[^/]+$/.test(path) ||
      path === 'user/authorization-mobile' ||
      path === 'user/authorization-by-custom-id'
    )
  ) {
    return 'employee_auth'
  }

  if (
    (method === 'put' && /^user\/[^/]+$/.test(path)) ||
    (method === 'post' && (
      path === 'external/add-child-to-class' ||
      path === 'external/add-child-to-classes-by-tag'
    ))
  ) {
    return 'student_action'
  }

  if (
    (method === 'post' && (
      path === 'student-journal/edit-grades' ||
      path === 'journal-beta/edit-grades'
    )) ||
    ((method === 'post' || method === 'delete') && /^student-journal\/delete-grade\/[^/]+$/.test(path))
  ) {
    return 'homework_grade'
  }

  if (
    method === 'get' &&
    (
      path === 'reports/left-students-report' ||
      path === 'report-home-work/statistics-excel' ||
      path === 'user/export' ||
      path === 'employees-export' ||
      path === 'employees-export-excel' ||
      path === 'reports/support-tickets-report-to-excel' ||
      /^crm-lead-history\/download-lead-history-excel\/[^/]+$/.test(path)
    )
  ) {
    return 'data_export'
  }

  return null
}

const inferMaxTwoFactorOperationByErrorText = (response?: AxiosResponse): string | null => {
  const errorText = getResponseErrorText(response).toLowerCase()

  if (errorText.includes('авторизац')) {
    return 'employee_auth'
  }

  if (
    errorText.includes('карточк') ||
    errorText.includes('учащ')
  ) {
    return 'student_action'
  }

  if (
    errorText.includes('дз') ||
    errorText.includes('кр') ||
    errorText.includes('пробник') ||
    errorText.includes('оцен')
  ) {
    return 'homework_grade'
  }

  if (
    errorText.includes('выгруз') ||
    errorText.includes('экспорт')
  ) {
    return 'data_export'
  }

  return null
}

const hasMaxTwoFactorConfirmedParam = (config?: AxiosError['config']): boolean => {
  const params = (config as any)?.params

  if (isEnabledFlag(params?.max_2fa_confirmed)) {
    return true
  }

  if (typeof config?.url === 'string') {
    try {
      const url = new URL(config.url, process.env.REACT_APP_PATH_FILE || 'http://localhost')
      if (isEnabledFlag(url.searchParams.get('max_2fa_confirmed'))) {
        return true
      }
    } catch {
      return config.url.includes('max_2fa_confirmed=1')
    }
  }

  return false
}

const withMaxTwoFactorConfirmedParam = (config: AxiosError['config']) => ({
  ...config,
  __maxTwoFactorRetry: true,
  params: {
    ...((config as any)?.params || {}),
    max_2fa_confirmed: 1,
  },
  skipGlobalErrorAlert: true,
})

const shouldSkipMaxTwoFactor = (config?: AxiosError['config']): boolean => {
  const path = normalizePath(config?.url || '')

  return (
    Boolean((config as any)?.__maxTwoFactorRetry) ||
    Boolean((config as any)?.skipMaxTwoFactor) ||
    hasMaxTwoFactorConfirmedParam(config) ||
    MAX_TWO_FACTOR_SKIP_PATHS.some((skipPath) => path === skipPath)
  )
}

const requestMaxTwoFactorConfirmation = ({
  api,
  operation,
  userId,
  description,
  botUrl,
}: {
  api: AxiosInstance
  operation: string
  userId: number
  description?: string
  botUrl?: string | null
}): Promise<boolean> => new Promise((resolve) => {
  store.dispatch(updateConfirm({
    title: MAX_TWO_FACTOR_OPERATION_TITLES[operation] || 'Подтвердите действие',
    loading: false,
    description: getMaxTwoFactorConfirmDescription(description),
    descriptionLink: botUrl || null,
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
    resetCodeKey: Date.now(),
    onCancel: () => resolve(false),
    onConfirm: async ({ code, setError }: { code: string; setError: (error: string) => void }) => {
      const verifyResponse = await api.post('two-factor/verify-code', {
        operation,
        user_id: userId,
        code,
      }, {
        skipGlobalErrorAlert: true,
      } as any)

      if (isFailedResponse(verifyResponse)) {
        setError(getResponseErrorText(verifyResponse))
        return false
      }

      resolve(true)
      return true
    },
  }))
})

const requestMaxLoginConfirmation = ({
  api,
  userId,
  botUrl,
}: {
  api: AxiosInstance
  userId: number
  botUrl?: string | null
}): Promise<boolean> => new Promise((resolve) => {
  const canDismiss = isCurrentUserAdmin()

  const confirmLoginCode = async ({ code, setError }: { code: string; setError: (error: string) => void }) => {
    const verifyResponse = await api.post('two-factor/verify-code', {
      operation: EMPLOYEE_AUTH_OPERATION,
      user_id: userId,
      code,
      force_confirmation: true,
    }, {
      skipGlobalErrorAlert: true,
      skipMaxTwoFactor: true,
    } as any)

    if (isFailedResponse(verifyResponse)) {
      setError(getResponseErrorText(verifyResponse))
      return false
    }

    resolve(true)
    return true
  }

  store.dispatch(updateConfirm({
    title: 'Подтвердите вход через MAX',
    loading: true,
    description: 'Отправляем код подтверждения...',
    descriptionLink: botUrl || null,
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
    blocking: !canDismiss,
    hideCancel: !canDismiss,
    resetCodeKey: Date.now(),
    onCancel: () => resolve(false),
    onConfirm: () => false,
  }))

  api.post('two-factor/request-code', {
    operation: EMPLOYEE_AUTH_OPERATION,
    user_id: userId,
    force_confirmation: true,
  }, {
    skipGlobalErrorAlert: true,
    skipMaxTwoFactor: true,
  } as any).then((requestCodeResponse) => {
    if (isFailedResponse(requestCodeResponse)) {
      store.dispatch(updateConfirm({
        loading: false,
        title: 'Введите код MAX',
        description: getResponseErrorText(requestCodeResponse),
        descriptionLink: botUrl || null,
        confirmText: 'Подтвердить',
        cancelText: 'Отмена',
        blocking: !canDismiss,
        hideCancel: !canDismiss,
        resetCodeKey: Date.now(),
        onCancel: () => resolve(false),
        onConfirm: confirmLoginCode,
      }))
      return
    }

    store.dispatch(updateConfirm({
      loading: false,
      title: 'Введите код MAX',
      description: MAX_LOGIN_CONFIRMATION_DESCRIPTION,
      descriptionLink: botUrl || null,
      confirmText: 'Подтвердить',
      cancelText: 'Отмена',
      blocking: !canDismiss,
      hideCancel: !canDismiss,
      resetCodeKey: Date.now(),
      onCancel: () => resolve(false),
      onConfirm: confirmLoginCode,
    }))
  }).catch((error) => {
    ErrorHandling.showAlert(getResponseErrorText(error))
    resolve(false)
  })
})

const getCurrentUserView = async (api: AxiosInstance, userId: number) => {
  const response = await api.get(`user/view/${userId}`, {
    skipGlobalErrorAlert: true,
    skipMaxTwoFactor: true,
  } as any)

  if (isFailedResponse(response)) return null

  const user = getResponsePayload(response)

  if (user) {
    store.dispatch(actionUpdProfile(user))
  }

  return user
}

const requestMaxBindingActivation = ({
  api,
  userId,
  blocking = true,
}: {
  api: AxiosInstance
  userId: number
  blocking?: boolean
}): Promise<boolean> => new Promise((resolve) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  store.dispatch(updateConfirm({
    loading: true,
    title: 'Активируйте MAX-бота',
    description: 'Готовим персональную ссылку для привязки MAX...',
    confirmText: 'Активировать',
    blocking,
    hideCancel: blocking,
    onCancel: () => resolve(false),
    onConfirm: () => false,
  }))

  api.post('max/start-activation', {
    user_id: userId,
    origin,
  }, {
    skipGlobalErrorAlert: true,
  } as any).then((activationResponse) => {
    if (isFailedResponse(activationResponse)) {
      ErrorHandling.showAlert(activationResponse?.data || 'Не удалось подготовить ссылку для активации MAX')
      resolve(false)
      return
    }

    const activationUrl = activationResponse?.data?.data?.activation_url

    store.dispatch(updateConfirm({
      loading: false,
      title: 'Введите код MAX',
      description: activationUrl
        ? MAX_BINDING_DESCRIPTION
        : 'Перейдите в MAX-бота, нажмите «Начать» и введите здесь 6-значный код активации.',
      descriptionLink: activationUrl ? String(activationUrl) : null,
      confirmText: 'Активировать',
      cancelText: 'Отмена',
      blocking,
      hideCancel: blocking,
      onCancel: () => resolve(false),
      onConfirm: async ({ code, setError }: { code: string; setError: (error: string) => void }) => {
        const activateResponse = await api.post('max/activate', {
          user_id: userId,
          code,
        }, {
          skipGlobalErrorAlert: true,
        } as any)

        if (isFailedResponse(activateResponse)) {
          setError(getResponseErrorText(activateResponse))
          return false
        }

        resolve(true)
        store.dispatch(refreshMessengerBindingStatus())
        return false
      },
    }))
  }).catch((error) => {
    ErrorHandling.showAlert(getResponseErrorText(error))
    resolve(false)
  })
})

const showMaxTwoFactorLoadingModal = ({
  operation,
  description,
  botUrl,
}: {
  operation: string
  description?: string
  botUrl?: string | null
}) => {
  store.dispatch(showConfirm({
    title: MAX_TWO_FACTOR_OPERATION_TITLES[operation] || 'Подтвердите действие',
    variant: 'twoFactor',
    codeLength: 6,
    description: description || 'Готовим отправку кода подтверждения в MAX...',
    descriptionLink: botUrl || null,
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
    loading: true,
    onCancel: () => {},
    onConfirm: () => false,
  }))
}

export const handleMaxTwoFactorResponse = async ({
  api,
  error,
}: {
  api: AxiosInstance
  error: AxiosError
}): Promise<AxiosResponse | AxiosError | null> => {
  await normalizeBinaryErrorResponseData(error)

  const originalRequest = error?.config
  const response = error?.response
  const responseData = response?.data as any

  if (
    !originalRequest ||
    shouldSkipMaxTwoFactor(originalRequest) ||
    (
      !isMaxBindingRequiredResponse(response) &&
      !isMaxTwoFactorRequiredResponse(response, originalRequest)
    )
  ) {
    return null
  }

  const userId = responseData?.meta?.user_id || store.getState()?.profile?.profile?.id

  if (isMaxBindingRequiredResponse(response)) {
    if (!userId) return null

    const activated = await requestMaxBindingActivation({
      api,
      userId,
      blocking: !isCurrentUserAdmin(),
    })

    if (!activated) {
      store.dispatch(hideConfirm())
      return error
    }

    const freshUser = await getCurrentUserView(api, Number(userId))

    if (freshUser?.requireMaxConfirmation) {
      const botUrl = await getMaxBotUrl(api)
      const confirmed = await requestMaxLoginConfirmation({
        api,
        userId: Number(userId),
        botUrl,
      })

      if (!confirmed) {
        store.dispatch(hideConfirm())
        return error
      }

      const confirmedUser = await getCurrentUserView(api, Number(userId))

      if (
        !isEnabledFlag(confirmedUser?.requireMaxBinding) &&
        !isEnabledFlag(confirmedUser?.requireMaxConfirmation)
      ) {
        reloadPageAfterMaxLoginConfirmation()
      }
    }

    store.dispatch(hideConfirm())

    return api({
      ...originalRequest,
      __maxTwoFactorRetry: true,
      skipGlobalErrorAlert: true,
    } as any)
  }

  const operation =
    responseData?.meta?.operation ||
    inferMaxTwoFactorOperation(originalRequest) ||
    inferMaxTwoFactorOperationByErrorText(response)

  if (!operation || !userId) {
    return null
  }

  showMaxTwoFactorLoadingModal({
    operation,
    description: responseData?.meta?.description,
  })

  const botUrl = await getMaxBotUrl(api)

  if (botUrl) {
    store.dispatch(updateConfirm({
      descriptionLink: botUrl,
    }))
  }

  const bindingResponse = await api.get(`max/check-is-active?user_id=${userId}`, {
    skipGlobalErrorAlert: true,
    skipMaxTwoFactor: true,
  } as any)

  if (isFailedResponse(bindingResponse) || bindingResponse?.data?.data?.is_active !== true) {
    if (isFailedResponse(bindingResponse)) {
      store.dispatch(hideConfirm())
      ErrorHandling.showAlert(getResponseErrorText(bindingResponse))
      return error
    }

    const activated = await requestMaxBindingActivation({
      api,
      userId,
      blocking: false,
    })

    if (!activated) {
      store.dispatch(hideConfirm())
      return error
    }

    store.dispatch(updateConfirm({
      loading: true,
      title: MAX_TWO_FACTOR_OPERATION_TITLES[operation] || 'Подтвердите действие',
      description: 'Отправляем код подтверждения действия в MAX...',
      descriptionLink: botUrl,
      resetCodeKey: Date.now(),
      confirmText: 'Подтвердить',
    }))
  }

  const requestCodeResponse = await api.post('two-factor/request-code', {
    operation,
    user_id: userId,
  }, {
    skipGlobalErrorAlert: true,
  } as any)

  if (isFailedResponse(requestCodeResponse)) {
    store.dispatch(hideConfirm())
    ErrorHandling.showAlert(requestCodeResponse?.data || 'Не удалось отправить код подтверждения MAX')
    return error
  }

  const confirmed = await requestMaxTwoFactorConfirmation({
    api,
    operation,
    userId,
    description: responseData?.meta?.description,
    botUrl,
  })

  if (!confirmed) {
    return error
  }

  return api(withMaxTwoFactorConfirmedParam(originalRequest) as any)
}
